/**
 * Gestion des erreurs pour les widgets Brydge
 *
 * Fournit les types d'erreurs, messages localisés et utilitaires
 * pour la détection et le traitement des erreurs API et réseau.
 *
 * @module hooks/useWidgetError
 */

import {useState, useCallback} from 'react';
import {logger} from '../../../utils/logger';

/**
 * Types d'erreurs possibles pour les widgets
 * @readonly
 * @enum {string}
 */
export const WIDGET_ERROR_TYPES = {
  API_KEY_MISSING: 'API_KEY_MISSING',
  API_KEY_INVALID: 'API_KEY_INVALID',
  COMPANY_NOT_FOUND: 'COMPANY_NOT_FOUND',
  STRIPE_NOT_CONFIGURED: 'STRIPE_NOT_CONFIGURED',
  GIFT_VOUCHERS_DISABLED: 'GIFT_VOUCHERS_DISABLED',
  SUBSCRIPTION_REQUIRED: 'SUBSCRIPTION_REQUIRED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

export const WIDGET_ERROR_MESSAGES = {
  [WIDGET_ERROR_TYPES.API_KEY_MISSING]: {
    title: 'Configuration manquante',
    message: 'La configuration du widget est incomplète.',
    details: 'Veuillez contacter l\'administrateur du site.',
  },
  [WIDGET_ERROR_TYPES.API_KEY_INVALID]: {
    title: 'Configuration incorrecte',
    message: 'Les paramètres de configuration ne sont pas valides.',
    details: 'Veuillez contacter l\'administrateur du site.',
  },
  [WIDGET_ERROR_TYPES.COMPANY_NOT_FOUND]: {
    title: 'Établissement introuvable',
    message: 'L\'établissement demandé n\'existe pas.',
    details: 'Veuillez vérifier l\'URL ou contacter l\'administrateur.',
  },
  [WIDGET_ERROR_TYPES.STRIPE_NOT_CONFIGURED]: {
    title: 'Paiement non configuré',
    message: 'Ce commerce n\'a pas encore configuré son système de paiement.',
    details: 'Veuillez contacter directement l\'établissement.',
  },
  [WIDGET_ERROR_TYPES.GIFT_VOUCHERS_DISABLED]: {
    title: 'Vente de bons cadeaux désactivée',
    message: 'La vente de bons cadeaux est actuellement désactivée pour cet établissement.',
    details: 'Veuillez contacter directement l\'établissement.',
    // Désactivation volontaire par l'établissement : état sobre (pas de cadrage
    // « erreur technique »), header brand et icône neutre plutôt qu'écran rouge.
    severity: 'unavailable',
  },
  [WIDGET_ERROR_TYPES.SUBSCRIPTION_REQUIRED]: {
    title: 'Service temporairement indisponible',
    message: 'Le système de réservation n\'est pas accessible actuellement.',
    details: 'Veuillez contacter directement l\'établissement pour effectuer votre réservation.',
  },
  [WIDGET_ERROR_TYPES.NETWORK_ERROR]: {
    title: 'Erreur de connexion',
    message: 'Impossible de se connecter au service.',
    details: 'Veuillez vérifier votre connexion internet et réessayer.',
  },
  [WIDGET_ERROR_TYPES.UNKNOWN_ERROR]: {
    title: 'Erreur inattendue',
    message: 'Une erreur inattendue est survenue.',
    details: 'Veuillez réessayer dans quelques instants.',
  },
};

/**
 * Hook de gestion des erreurs pour les widgets
 *
 * Fournit un état d'erreur et des fonctions pour gérer les erreurs
 * de manière centralisée dans les composants widget.
 *
 * @return {Object} Objet contenant l'état d'erreur et les fonctions de gestion
 * @return {Error|null} return.error - Erreur actuelle ou null
 * @return {Function} return.handleError - Fonction pour traiter une erreur
 * @return {Function} return.clearError - Fonction pour effacer l'erreur
 * @return {boolean} return.hasError - Indique si une erreur est présente
 */
export function useWidgetError() {
  const [error, setError] = useState(null);

  const handleError = useCallback((apiError) => {
    logger.error('Widget Error Handler:', apiError);
    setError(apiError);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    error,
    handleError,
    clearError,
    hasError: !!error,
  };
}

/**
 * Crée un objet d'erreur Stripe synthétique pour une gestion unifiée
 *
 * Utilisé pour générer une erreur formatée lorsque Stripe n'est pas
 * configuré pour un widget donné.
 *
 * @param {string} widgetType - Type de widget (reservations, giftvouchers, etc.)
 * @return {Object} Objet d'erreur synthétique avec propriétés standardisées
 */
export function createStripeNotConfiguredError(widgetType = 'generic') {
  return {
    isStripeError: true,
    widgetType,
    message: 'Stripe account not configured',
    response: {
      status: 422,
      data: {
        code: 'STRIPE_NOT_CONFIGURED',
        message: 'Stripe account not configured',
      },
    },
  };
}

/**
 * Crée un objet d'erreur synthétique « bons cadeaux désactivés »
 *
 * Utilisé lorsque le module bons cadeaux est désactivé ou que la vente en ligne
 * est coupée pour l'établissement, afin d'unifier le rendu via WidgetStateHandler.
 *
 * Les textes affichés (titre, message) sont résolus par l'appelant sur la langue
 * courante du widget — même source que la page de vente publique (clés i18n
 * `giftVoucher.unavailable.*`), avec le message personnalisé de l'établissement
 * (`onlineSaleDisabledMessage`) en surcharge éventuelle du message par défaut.
 *
 * @param {string} widgetType - Type de widget (giftvouchers, etc.)
 * @param {Object} [texts] - Textes déjà localisés
 * @param {string|null} [texts.title] - Titre affiché
 * @param {string|null} [texts.message] - Message affiché (personnalisé ou défaut i18n)
 * @return {Object} Objet d'erreur synthétique avec propriétés standardisées
 */
export function createGiftVouchersDisabledError(widgetType = 'generic', {title = null, message = null} = {}) {
  return {
    isGiftVouchersDisabledError: true,
    widgetType,
    // Textes résolus par l'appelant (i18n courant + éventuel message personnalisé)
    displayTitle: title || null,
    displayMessage: message || null,
    message: 'Gift vouchers disabled',
    response: {
      status: 403,
      data: {
        code: 'GIFT_VOUCHERS_DISABLED',
        message: 'Gift vouchers disabled',
      },
    },
  };
}

/**
 * Extrait les informations d'erreur à partir d'une erreur API
 *
 * Fonction principale utilisée par les composants widget pour
 * obtenir un message d'erreur formaté et localisé.
 *
 * @param {Error} error - Objet d'erreur API
 * @return {Object|null} Informations d'erreur avec titre, message et détails
 */
export function getErrorInfo(error) {
  if (!error) return null;

  // Vérification d'une erreur synthétique « bons cadeaux désactivés »
  if (error?.isGiftVouchersDisabledError) {
    const baseInfo = WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.GIFT_VOUCHERS_DISABLED];
    // Titre et message sont fournis résolus par l'appelant (i18n courant +
    // éventuel message personnalisé de l'établissement) ; on aligne le rendu sur
    // la page publique : titre + message seul, sans ligne de détails générique.
    return {
      ...baseInfo,
      ...(error.displayTitle ? {title: error.displayTitle} : {}),
      ...(error.displayMessage ? {message: error.displayMessage, details: ''} : {}),
    };
  }

  // Vérification d'une erreur Stripe synthétique
  if (error?.isStripeError) {
    return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.STRIPE_NOT_CONFIGURED];
  }

  // Vérification des erreurs côté client (avant appel API)
  if (error?.message) {
    const message = error.message.toLowerCase();

    // Détection des problèmes de clé API côté client
    if (message.includes('api key is missing') ||
        message.includes('missing companyid or apikey') ||
        message.includes('api key manquante')) {
      return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.API_KEY_MISSING];
    } else if (message.includes('api key') &&
             (message.includes('invalid') || message.includes('invalide'))) {
      return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.API_KEY_INVALID];
    }
  }

  // Analyse du statut HTTP pour les réponses d'erreur
  if (error?.response) {
    const {status, data} = error.response;

    switch (status) {
      case 401:
        return data?.code === 'API_KEY_MISSING' ?
          WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.API_KEY_MISSING] :
          WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.API_KEY_INVALID];
      case 403:
        // Vérifie si c'est une erreur d'abonnement requis
        if (data?.code === 'SUBSCRIPTION_REQUIRED') {
          return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.SUBSCRIPTION_REQUIRED];
        }
        return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.API_KEY_INVALID];
      case 404:
        return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.COMPANY_NOT_FOUND];
      case 422:
        if (data?.code === 'STRIPE_NOT_CONFIGURED') {
          return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.STRIPE_NOT_CONFIGURED];
        }
        break;
      default:
        return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.NETWORK_ERROR];
    }
  }

  // Erreur réseau sans réponse du serveur
  if (error?.code === 'NETWORK_ERROR') {
    return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.NETWORK_ERROR];
  }

  return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.UNKNOWN_ERROR];
}
